import { formatDate } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { Realisation } from 'src/app/classes/realisation';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { Settings } from 'src/app/settings.class';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { OwnHourEditAllowedPipe } from '../hour-registration/hour-edit-allowed.pipe';

@Pipe({
    name: 'notEditable',
})
export class NotEditablePipe implements PipeTransform {
    transform(realisation?: Realisation, date?: Date): string | void {
        if (!realisation && !date) {
            return;
        }

        if (AuthorisationService.hasFunctionality(Functionalities.CommonHourCheck) && !realisation?.paid) {
            return;
        }

        if (realisation?.paid) {
            return 'Niet aanpasbaar omdat de uren al uitbetaald zijn';
        }

        if (
            realisation?.approved &&
            AuthorisationService.hasFunctionality(Functionalities.HourTimeEdit) &&
            !AuthorisationService.canApproveHours()
        ) {
            return 'Niet aanpasbaar omdat de uren al goedgekeurd zijn';
        }

        if (realisation) {
            const currentUser = LocalStorage.getUser();
            const currentUserId = currentUser?.id;
            const canEditOwnHours =
                !realisation.approved &&
                Number(realisation.user_id) === currentUserId &&
                AuthorisationService.hasAnyFunctionality([Functionalities.Hours, Functionalities.HoursRestricted]);
            const canEditTimes =
                !!realisation.id &&
                !realisation.approved &&
                AuthorisationService.hasFunctionality(Functionalities.HourTimeEdit);
            const userIsCheckedBySomeoneElse =
                Number(realisation.user?.id) !== currentUserId &&
                !!(realisation.user?.hours_checked_by || realisation.user?.hours_checked_by_role) &&
                !!realisation.user &&
                !AuthorisationService.canCheckUserHours(realisation.user);
            const hasPlanningPerformer =
                realisation.user_planning?.performer_id != null || realisation.planning_has?.performer_id != null;
            const project =
                realisation.project ??
                realisation.planning_has?.project ??
                realisation.user_planning?.project ??
                realisation.planning?.project;
            const canManageAsHourChecker =
                AuthorisationService.canApproveHours() &&
                !userIsCheckedBySomeoneElse &&
                (realisation.performer_id != null
                    ? Number(realisation.performer_id) === currentUserId
                    : (!!realisation.user && AuthorisationService.canCheckUserHours(realisation.user)) ||
                      Number(realisation.user_planning?.performer_id) === currentUserId ||
                      Number(realisation.planning_has?.performer_id) === currentUserId ||
                      (!hasPlanningPerformer &&
                          (Number(realisation.saved_by_id) === currentUserId ||
                              Number(realisation.user_id) === currentUserId ||
                              Number(realisation.planning_has?.driver_user_id) === currentUserId ||
                              Number(realisation.planning?.performer) === currentUserId ||
                              Number(realisation.planning?.asphalt_performer) === currentUserId ||
                              Number(project?.performer_id) === currentUserId ||
                              Number(project?.asphalt_performer_id) === currentUserId)));

            if (!canEditOwnHours && !canEditTimes && !canManageAsHourChecker) {
                return 'Geen rechten om deze uren aan te passen';
            }
        }

        const beginDate = new Date(realisation?.begindate ?? date!);
        const today = new Date();
        const endOfDay = Utils.setTime(new Date(today), 23, 59);
        if (endOfDay.getTime() < beginDate.getTime()) {
            return 'Niet aanpasbaar omdat de uren in de toekomst liggen';
        }

        // Always allow editing hours if OwnHourEditAllowedPipe allows it
        // That's the case when user is owner and within allowed edit time, OR when Features.CommonHourCheck is set and realisation is not paid
        if (realisation && new OwnHourEditAllowedPipe().transform(realisation)) {
            return;
        }
        if (
            realisation?.user_id === LocalStorage.getUser()?.id &&
            realisation.approved &&
            (LocalStorage.getUser()?.hours_checked_by || LocalStorage.getUser()?.hours_checked_by_role)
        ) {
            return 'Niet aanpasbaar omdat de uren al goedgekeurd zijn';
        }

        // When realisation not submitted, only allow editing hours if user is the owner
        if (
            realisation &&
            realisation.user_id &&
            LocalStorage.user.id !== realisation.user_id &&
            !realisation.submitted &&
            AuthorisationService.hasAnyFunctionality(
                [Functionalities.Hours, Functionalities.HoursRestricted],
                realisation.user
            )
        ) {
            const diff = (today.getTime() - beginDate.getTime()) / 1000 / 60 / 60;
            const maxDiff = Settings.HOUR_EDIT_ALLOWED_HOURS;
            if (diff < maxDiff) {
                return 'Niet aanpasbaar omdat de uren niet zijn ingediend';
            }
        }

        const registerDate = Utils.setTime(new Date(beginDate), 23, 59);

        const editUntil = Utils.setTime(new Date(today), 0, 0);
        // daysBack = this week, added 7 days if today is before thursday, minus 1 day because week starts on monday
        const editUntilDayPrevWeek = 3;
        const editBeforeHour = 6;
        const mayEditPrevWeek =
            today.getDay() < editUntilDayPrevWeek ||
            (today.getDay() === editUntilDayPrevWeek && today.getHours() < editBeforeHour);
        const daysBack = today.getDay() + (mayEditPrevWeek ? 7 : 0) - 1;

        editUntil.setDate(editUntil.getDate() - daysBack);
        // If functionality HoursExtended is set, allow editing hours for an extra 3 weeks
        if (AuthorisationService.hasFunctionality(Functionalities.HoursExtended)) {
            editUntil.setDate(editUntil.getDate() - 21);
        }

        const performerDeadline = Utils.newDate(realisation?.performer_forward_deadline_at);
        const performerDeadlineExpired = performerDeadline && Utils.isPast(performerDeadline);

        if (registerDate.getTime() < editUntil.getTime() && (performerDeadlineExpired || !performerDeadline)) {
            return `Niet aanpasbaar omdat de uren voor ${formatDate(editUntil, 'EEE dd MMM y', 'nl')} zijn`;
        }
        return;
    }
}
